import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { BaseModule } from '../../../core/base/base.module';
import { PaymentMethodService } from '../../../pages/application/settings/payment-method/payment-method.service';
import { PaymentMethodModel } from '../../../core/models/payment-method.model';

@Component({
  selector: 'payment-method-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BaseModule],
  templateUrl: './payment-method-selector.component.html',
  styleUrls: ['./payment-method-selector.component.scss'],
})
export class PaymentMethodSelectorComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() paymentMethodControlName = 'paymentMethod';
  @Input() ibanControlName = 'iban';
  @Input() bicControlName = 'bic';
  @Input() disabled = false;

  paymentMethods$: Observable<PaymentMethodModel[]>;
  showBankDetails = false;

  private destroy$ = new Subject<void>();

  constructor(private paymentMethodService: PaymentMethodService) {}

  ngOnInit() {
    this.loadPaymentMethods();
    this.setupBankDetailsVisibility();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPaymentMethods(search = '') {
    this.paymentMethods$ = this.paymentMethodService
      .findAll(null, null, 'label', 'asc', search, {})
      .pipe(map(res => res.data));
  }

  onPaymentMethodSearch(search: string) {
    this.loadPaymentMethods(search);
  }

  private setupBankDetailsVisibility() {
    const control = this.formGroup.get(this.paymentMethodControlName);

    if (control?.value) {
      this.showBankDetails = !!control.value.requiresBankDetails;
      this.updateBankDetailsValidators(this.showBankDetails);
    }

    control?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value && typeof value === 'object') {
        this.showBankDetails = !!value.requiresBankDetails;
        this.updateBankDetailsValidators(this.showBankDetails);
      } else if (!value) {
        this.showBankDetails = false;
        this.updateBankDetailsValidators(false);
        this.formGroup.get(this.ibanControlName)?.reset();
        this.formGroup.get(this.bicControlName)?.reset();
      }
    });
  }

  private updateBankDetailsValidators(required: boolean) {
    const ibanControl = this.formGroup.get(this.ibanControlName);
    const bicControl = this.formGroup.get(this.bicControlName);

    if (required) {
      ibanControl?.setValidators([Validators.required]);
      bicControl?.setValidators([Validators.required]);
    } else {
      ibanControl?.clearValidators();
      bicControl?.clearValidators();
    }

    ibanControl?.updateValueAndValidity();
    bicControl?.updateValueAndValidity();
  }
}
